import { Alert, Badge, Box, Button, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import type { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo, useState } from "react";
import { openapi } from "../../../../../openapi";
import type {
  GetAppadminMcappsInitialsAccessApiResponse,
  GetAppadminMcappsProjectAccessApiResponse,
} from "../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";

type ProjectMemberAccessRow = GetAppadminMcappsProjectAccessApiResponse["access"][number];
type InitialsProjectAccessRow = GetAppadminMcappsInitialsAccessApiResponse["projects"][number];

export const McAppsAccessAdmin: React.FC = () => {
  const [projectNoInput, setProjectNoInput] = useState("");
  const [initialsInput, setInitialsInput] = useState("");
  const [fetchProjectAccess, { data: projectAccessData, error: projectAccessError, isFetching: isFetchingProjectAccess }] =
    openapi.endpoints.getAppadminMcappsProjectAccess.useLazyQuery();
  const [
    fetchInitialsAccess,
    { data: initialsAccessData, error: initialsAccessError, isFetching: isFetchingInitialsAccess },
  ] = openapi.endpoints.getAppadminMcappsInitialsAccess.useLazyQuery();

  const projectNo = useMemo(() => {
    const parsed = Number(projectNoInput.trim());
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [projectNoInput]);

  const initials = useMemo(() => {
    const trimmed = initialsInput.trim();
    return trimmed.length > 0 ? trimmed.toLowerCase() : null;
  }, [initialsInput]);

  const projectAccessErrorMessage =
    (projectAccessError as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (projectAccessError as { message?: string } | undefined)?.message;

  const initialsAccessErrorMessage =
    (initialsAccessError as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
    (initialsAccessError as { message?: string } | undefined)?.message;

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    };
  }, []);

  const projectMemberColumnDefs = useMemo<ColDef<ProjectMemberAccessRow>[]>(
    () => [
      {
        headerName: "Name",
        flex: 1,
        minWidth: 190,
        valueGetter: (params) => params.data?.user?.name ?? "",
      },
      {
        headerName: "Initials",
        width: 110,
        valueGetter: (params) => params.data?.user?.initials ?? "",
      },
      {
        headerName: "Employee no.",
        width: 140,
        valueGetter: (params) => params.data?.user?.employeeNumber ?? "",
      },
      {
        headerName: "Email",
        flex: 1,
        minWidth: 220,
        valueGetter: (params) => params.data?.user?.email ?? "",
      },
      {
        field: "group",
        headerName: "Group",
        width: 125,
      },
      {
        field: "disciplin",
        headerName: "Discipline",
        width: 145,
      },
      {
        field: "isPassive",
        headerName: "Passive",
        width: 115,
        valueFormatter: (params) => (params.value === true ? "Yes" : "No"),
      },
      {
        headerName: "Status",
        width: 135,
        valueGetter: (params) => params.data?.user?.maconomyStatus ?? "",
      },
      {
        headerName: "Location",
        width: 150,
        valueGetter: (params) => params.data?.user?.location ?? "",
      },
    ],
    [],
  );

  const initialsProjectColumnDefs = useMemo<ColDef<InitialsProjectAccessRow>[]>(
    () => [
      {
        headerName: "Project no.",
        width: 135,
        valueGetter: (params) => params.data?.project.maconomy.projectNo ?? "",
      },
      {
        headerName: "Project",
        flex: 1,
        minWidth: 240,
        valueGetter: (params) => params.data?.project.maconomy.name ?? "",
      },
      {
        headerName: "Domain owner",
        width: 160,
        valueGetter: (params) => params.data?.project.domainOwner ?? "",
      },
      {
        field: "group",
        headerName: "Group",
        width: 125,
      },
      {
        field: "disciplin",
        headerName: "Discipline",
        width: 145,
      },
      {
        field: "isPassive",
        headerName: "Passive",
        width: 115,
        valueFormatter: (params) => (params.value === true ? "Yes" : "No"),
      },
    ],
    [],
  );

  const searchProjectAccess = () => {
    if (projectNo == null) return;
    fetchProjectAccess({ projectNo });
  };

  const searchInitialsAccess = () => {
    if (initials == null) return;
    fetchInitialsAccess({ initials });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Title order={3}>MCApps access lookup</Title>
          <Text size="sm" c="dimmed">
            Search project members from MCApps projects and users by project number or initials.
          </Text>
        </Stack>
        {projectAccessData != null && (
          <Badge variant="light" color="green">
            {projectAccessData.access.length} members
          </Badge>
        )}
      </Group>

      <Paper withBorder p="sm" radius="sm">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <TextInput
              label="Project number"
              placeholder="10242425"
              value={projectNoInput}
              onChange={(event) => setProjectNoInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") searchProjectAccess();
              }}
              style={{ flex: "1 1 18rem" }}
            />
            <Button loading={isFetchingProjectAccess} disabled={projectNo == null} onClick={searchProjectAccess}>
              Search project
            </Button>
          </Group>

          {projectAccessErrorMessage != null && (
            <Alert color="red" variant="light">
              {projectAccessErrorMessage}
            </Alert>
          )}

          {projectAccessData != null && (
            <>
              <Group gap="xl" align="flex-start">
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Project
                  </Text>
                  <Text fw={600}>{projectAccessData.project.maconomy.name}</Text>
                  <Text size="sm" c="dimmed">
                    {projectAccessData.project.maconomy.projectNo}
                  </Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Domain owner
                  </Text>
                  <Text size="sm">{projectAccessData.project.domainOwner ?? ""}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Members
                  </Text>
                  <Text size="sm">{projectAccessData.access.length}</Text>
                </Stack>
              </Group>

              <Box className="ag-theme-quartz" h={420} w="100%">
                <AgGridReact<ProjectMemberAccessRow>
                  rowData={projectAccessData.access}
                  columnDefs={projectMemberColumnDefs}
                  defaultColDef={defaultColDef}
                  loading={isFetchingProjectAccess}
                  animateRows={false}
                  suppressCellFocus
                  suppressContextMenu
                  localeText={AG_GRID_LOCALE_NOR}
                  getRowId={(params) => params.data.memberId}
                  overlayNoRowsTemplate="No members found for this project."
                />
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="sm">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-end">
            <TextInput
              label="Initials"
              placeholder="abc"
              value={initialsInput}
              onChange={(event) => setInitialsInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") searchInitialsAccess();
              }}
              style={{ flex: "1 1 18rem" }}
            />
            <Button loading={isFetchingInitialsAccess} disabled={initials == null} onClick={searchInitialsAccess}>
              Search user
            </Button>
          </Group>

          {initialsAccessErrorMessage != null && (
            <Alert color="red" variant="light">
              {initialsAccessErrorMessage}
            </Alert>
          )}

          {initialsAccessData != null && (
            <>
              <Group gap="xl" align="flex-start">
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    User
                  </Text>
                  <Text fw={600}>{initialsAccessData.users[0]?.name ?? initialsAccessData.initials}</Text>
                  <Text size="sm" c="dimmed">
                    {initialsAccessData.users.map((user) => user.email).join(", ")}
                  </Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Initials
                  </Text>
                  <Text size="sm">{initialsAccessData.initials}</Text>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Projects
                  </Text>
                  <Text size="sm">{initialsAccessData.projects.length}</Text>
                </Stack>
              </Group>

              <Box
                component="pre"
                bg="gray.0"
                p="sm"
                style={{
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: 4,
                  fontSize: 12,
                  margin: 0,
                  maxHeight: 260,
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                }}
              >
                {JSON.stringify(initialsAccessData.users, null, 2)}
              </Box>

              <Box className="ag-theme-quartz" h={420} w="100%">
                <AgGridReact<InitialsProjectAccessRow>
                  rowData={initialsAccessData.projects}
                  columnDefs={initialsProjectColumnDefs}
                  defaultColDef={defaultColDef}
                  loading={isFetchingInitialsAccess}
                  animateRows={false}
                  suppressCellFocus
                  suppressContextMenu
                  localeText={AG_GRID_LOCALE_NOR}
                  getRowId={(params) => params.data.memberId}
                  overlayNoRowsTemplate="No project access rows found for this user."
                />
              </Box>
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
